import React from 'react';
import moment from 'moment';
import {
  object, string, ref, array,
} from 'yup';
import _, { cloneDeep, startCase, get } from 'lodash';

import { Tabs, Tab } from 'react-bootstrap';
import { env } from '~/commons/config';
import FormGroup from '~/commons/components/FormGroup.jsx';

import FormResource from './FormResource.jsx';
import FieldList from './FieldList.jsx';
import FieldArrayCards from './FieldArrayCards.jsx';

const orgIds = [
  ...(get(env, 'user.orgIds') || []),
  get(env, 'user.showroom.orgId') || '',
].filter(Boolean);

const statusList = env?.status?.stockTrans || [];

const schema = (lf, doc) => object({
  status: string().required(),
  items: array().min(1, 'Please choose at least 1 stock at Stock tab'),
  ...(doc ? {} : {
    from: string().required(),
    to: string()
      .required('To is a mandatory field')
      .when('from', {
        is: (val) => val,
        then: (s) => s
          .notOneOf(
            [ref('from')],
            'From and To cannot be the same',
          ),
      }),
  }),
});
const defaults = (lf) => ({
  refCode: '',
  items: [],
  status: statusList[0] || '',
  from: _.find(lf, (x) => x._id === get(env, 'user.showroom._id'))?._id ?? lf?.[0]?._id ?? '',
  to: '',
  createdBy: env.user ?? {
    username: '',
  },
  receivedBy: '',
  createdAt: '',
  remarks: '',
  provider: '',
  trackingNo: '',
  esd: '',
});

const FormStockTrans = ({ stocktrans, showrooms = [] }) => {
  const api = '/api/core/stocks/trans';
  const limitedFrom = showrooms.filter(
    ({ orgId }) => (orgIds.length ? orgIds.includes(orgId) : true),
  );
  const srid = get(env, 'user.showroom._id');
  const permitAction = [stocktrans?.to, stocktrans?.from]
    .map(
      (v, i) => (v && (srid ? v._id === srid : !orgIds.length || orgIds.includes(v.orgId))
        ? statusList[i + 1]
        : null),
    )
    .filter(Boolean);
  permitAction.unshift(statusList[0]);

  return (
    <FormResource
      {...{
        defaults: defaults(limitedFrom),
        schema: schema(limitedFrom, stocktrans),
        forceReload: true,
        stored: stocktrans
          ? (() => {
            const r = cloneDeep(stocktrans);
            if (r.createdAt) {
              r.createdAt = moment(r.createdAt).format(
                env.f.dt,
              );
            }
            if (r.esd) {
              r.esd = moment(r.esd).format('YYYY-MM-DD');
            }
            return r;
          })()
          : undefined,
        api,
        initialValuesOpts: { omitIds: false },
        onSubmit: (v) => {
          const r = cloneDeep(v);
          if (r.esd) {
            r.esd = moment(r.esd).startOf('days').toISOString();
          }
          r.items = r.items.map((i) => ({
            ...i,
            item: i.item?._id,
          }));
          return r;
        },
      }}>
      {({ values }) => (
        <Tabs defaultActiveKey="basic" unmountOnExit>
          <Tab eventKey="basic" title="Basic">
            <br />
            <div className="row">
              <div className="col">
                <FormGroup name="refCode" label='Stock Transfer Number' disabled />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <FormGroup name="status" as="select" disabled>
                  {statusList.map((v) => (
                    <option key={v} value={v}>
                      {startCase(v)}
                    </option>
                  ))}
                </FormGroup>
              </div>
            </div>
            <div className="row">
              <div className="col">
                {stocktrans ? (
                  <div className='form-group'>
                    <label>From</label>
                    <input className="form-control" disabled value={`${stocktrans?.from?.orgId ?? ''} - ${stocktrans?.from?.showroom ?? ''}`} />
                  </div>
                ) : (
                  <FormGroup
                    name="from"
                    disabled={!!get(env, 'user.showroom')}
                    as="select">
                    <option value="">-</option>
                    {showrooms.filter((x) => _.includes(orgIds, x.orgId)).map(
                      (v) => (
                        <option value={v._id} key={v._id}>
                          {`${v.orgId} - ${v.showroom}`}
                        </option>
                      ),
                    )}
                  </FormGroup>
                )}
              </div>
              <div className="col">
                {stocktrans ? (
                  <div className='form-group'>
                    <label>To</label>
                    <input className="form-control" disabled value={`${stocktrans?.to?.orgId ?? ''} - ${stocktrans?.to?.showroom ?? ''}`} />
                  </div>
                ) : (
                  <FormGroup
                    name="to"
                    as="select"
                    disabled={stocktrans}>
                    <option value="">-</option>
                    {showrooms.filter((x) => x._id !== values.from).map((v) => (
                      <option value={v._id} key={v._id}>
                        {`${v.orgId} - ${v.showroom}`}
                      </option>
                    ))}
                  </FormGroup>
                )}
              </div>
            </div>
            <div className="row">
              <div className="col">
                <FormGroup name="provider" />
              </div>
              <div className="col">
                <FormGroup name="trackingNo" />
              </div>
              <div className="col">
                <FormGroup
                  name="esd"
                  label="Estimated Shipping Date"
                  type="date"
                  min={moment().format('YYYY-MM-DD')}
                />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <FormGroup
                  name="createdBy.username"
                  label="Created By"
                  disabled
                />
              </div>
              <div className="col">
                <FormGroup name="receivedBy" />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <FormGroup name="remarks" as="textarea" />
              </div>
            </div>
          </Tab>
          <Tab eventKey="stocks" title="Stocks">
            <div className="py-3">
              <FieldList
                {...{
                  name: 'items',
                  segment: 'stocks',
                  disabled: stocktrans,
                  list: {
                    api: '/api/core/stocks',
                    helperText: 'Click to add stock',
                    filter: {
                      status: 'Active',
                      showroom: get(env, 'user.showroom._id') ?? orgIds,
                    },
                    searchable: true,
                    onRowClick: (props) => {
                      const { arr, row, helper } = props;
                      const ids = arr.map(
                        ({ item }) => item?._id,
                      );
                      if (ids.includes(row?._id)) {
                        return null;
                      }
                      return helper.push({
                        item: row,
                        status: statusList[0],
                        remarks: '',
                      });
                    },
                    cols: [
                      {
                        key: 'itemCode',
                        label: 'Item Code',
                        sortable: true,
                      },
                      {
                        key: 'serialNo',
                        label: 'Serial No',
                        sortable: true,
                      },
                      {
                        key: 'prices.retailPrice',
                        children: 'Retail Price',
                        sortable: true,
                        getter: (v) => (Number(v) || 0).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }),
                      },
                      {
                        key: 'location',
                        children: 'Location',
                        sortable: true,
                      },
                    ],
                  },
                }}>
                {({ formStatus }) => (
                  <FieldArrayCards
                    name="items"
                    disabled={
                      formStatus?.disabled || stocktrans
                    }>
                    {(v, i) => (
                      <>
                        {stocktrans?.items?.[i]?.status
                          === statusList[0] ? (
                          <FormGroup
                            name={`items[${i}].status`}
                            label="Status"
                            as="select">
                            {permitAction.map((x) => (
                              <option key={x} value={x}>
                                {startCase(x)}
                              </option>
                            ))}
                          </FormGroup>
                        ) : (
                          <FormGroup
                            name={`items[${i}].status`}
                            label="Status"
                            disabled
                          />
                        )}

                        <div className="row">
                          <div className="col">
                            <FormGroup
                              name={`items[${i}].item.itemCode`}
                              label="Item Code"
                              disabled
                            />
                          </div>
                          <div className="col">
                            <FormGroup
                              name={`items[${i}].item.serialNo`}
                              label="Serial No"
                              disabled
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormGroup
                              name={`items[${i}].remarks`}
                              label="Remarks"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </FieldArrayCards>
                )}
              </FieldList>
            </div>
          </Tab>
        </Tabs>
      )}
    </FormResource>
  );
};

export default FormStockTrans;
